import { useCallback, useEffect, useState } from 'react';
import { apiGet, type Overview as OverviewData } from './api';
import { getCreds, clearCreds } from './session';
import { useHashRoute, navigate } from './hooks';
import { Login } from './components/Login';
import { Shell } from './components/Shell';
import { Overview } from './screens/Overview';
import { Applications } from './screens/Applications';
import { Members } from './screens/Members';
import { MemberDetail } from './screens/MemberDetail';
import { MoneyScreen } from './screens/Money';
import { Payouts } from './screens/Payouts';
import { Moderation } from './screens/Moderation';
import { Reports } from './screens/Reports';
import { Game } from './screens/Game';

export function App() {
  const [signedIn, setSignedIn] = useState(() => getCreds() != null);
  const route = useHashRoute();
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [overviewTick, setOverviewTick] = useState(0);

  // A 401 anywhere signs us out (dispatched by the useAsync hook).
  useEffect(() => {
    const onSignout = () => setSignedIn(false);
    window.addEventListener('hydrus-admin-signout', onSignout);
    return () => window.removeEventListener('hydrus-admin-signout', onSignout);
  }, []);

  // Keep the nav badge counts fresh; refreshed after any mutating action via bumpOverview.
  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    apiGet<OverviewData>('/v1/admin/overview')
      .then((o) => live && setOverview(o))
      .catch(() => {
        /* badges are best-effort; screens surface real errors */
      });
    return () => {
      live = false;
    };
  }, [signedIn, overviewTick]);

  const bumpOverview = useCallback(() => setOverviewTick((t) => t + 1), []);

  function signOut() {
    clearCreds();
    setOverview(null);
    setSignedIn(false);
    navigate('overview');
  }

  if (!signedIn) {
    return (
      <Login
        onSignedIn={() => {
          setSignedIn(true);
          bumpOverview();
        }}
      />
    );
  }

  const active = route[0] || 'overview';

  return (
    <Shell active={active} overview={overview} onSignOut={signOut}>
      {renderRoute(route, bumpOverview)}
    </Shell>
  );
}

function renderRoute(route: string[], bump: () => void) {
  const [head, second] = route;
  switch (head) {
    case undefined:
    case '':
    case 'overview':
      return <Overview />;
    case 'applications':
      return <Applications onChange={bump} />;
    case 'members':
      return second ? <MemberDetail id={second} onChange={bump} /> : <Members onChange={bump} />;
    case 'money':
      return <MoneyScreen onChange={bump} />;
    case 'payouts':
      return <Payouts />;
    case 'moderation':
      return <Moderation onChange={bump} />;
    case 'reports':
      return <Reports />;
    case 'game':
      return <Game onChange={bump} />;
    default:
      return <Overview />;
  }
}
